"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch } from "@/lib/api-client";
import type { Reservation } from "@/lib/api-client";
import { useAppStore, currentView } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarX, CalendarCheck, Clock } from "lucide-react";
import {
  formatDate,
  formatPrice,
  nightsBetween,
  statusBadgeClass,
  statusLabel,
  nightLabel,
  bookingTypeLabel,
  bookingTypeBadgeClass,
} from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";
import { apiPost, apiDelete } from "@/lib/api-client";
import { Plus, Lock, X } from "lucide-react";
import type { StayMember } from "@/lib/api-client";

export function MyReservationsView() {
  const user = useAppStore((s) => s.user);
  const openAuth = useAppStore((s) => s.openAuth);
  const openProduct = useAppStore((s) => s.openProduct);
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [tab, setTab] = useState("upcoming");
  // Member management on the user's own private stays.
  const [addFor, setAddFor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: memberData } = useQuery({
    queryKey: ["members"],
    queryFn: () => apiGet<{ members: { id: string; name: string; isSelf: boolean }[] }>("/api/members"),
    enabled: !!user,
  });
  const directory = memberData?.members ?? [];

  const addMember = async (reservationId: string, userId: string) => {
    setBusy(true);
    try {
      await apiPost(`/api/reservations/${reservationId}/participants`, { userId });
      toast.success(t("stay.memberAdded"));
      setAddFor(null);
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || "Could not add the member");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (reservationId: string, userId: string) => {
    setBusy(true);
    try {
      await apiDelete(
        `/api/reservations/${reservationId}/participants?userId=${encodeURIComponent(userId)}`
      );
      toast.success(t("stay.memberRemoved"));
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || "Could not remove the member");
    } finally {
      setBusy(false);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ["reservations", "mine"],
    queryFn: () => apiGet<{ reservations: Reservation[] }>("/api/reservations"),
    enabled: !!user,
  });

  const all = data?.reservations ?? [];

  const cancel = async (id: string) => {
    try {
      await apiPatch(`/api/reservations/${id}`, { action: "cancel" });
      toast.success(t("toast.reservationCancelled"));
      qc.invalidateQueries({ queryKey: ["reservations"] });
    } catch (err: any) {
      toast.error(err.message || t("toast.couldNotCancel"));
    }
  };

  if (!user) {
    return (
      <div className="mx-auto grid max-w-7xl place-items-center px-4 py-24 text-center sm:px-6">
        <CalendarCheck className="mb-4 h-12 w-12 text-muted-foreground" />
        <h2 className="text-xl font-semibold">{t("trips.signInTitle")}</h2>
        <p className="mt-1 text-muted-foreground">{t("trips.signInBody")}</p>
        <Button className="mt-4" onClick={() => openAuth("login")}>
          {t("common.signin")}
        </Button>
      </div>
    );
  }

  const now = Date.now();
  const upcoming = all.filter(
    (r) => new Date(r.endDate).getTime() >= now && r.status !== "CANCELLED"
  );
  const past = all.filter(
    (r) => new Date(r.endDate).getTime() < now || r.status === "CANCELLED"
  );

  const list = tab === "upcoming" ? upcoming : past;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-bold tracking-tight">{t("trips.title")}</h1>
      <p className="mt-1 text-muted-foreground">{t("trips.subtitle")}</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList>
          <TabsTrigger value="upcoming">
            {t("trips.upcoming")} ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="past">
            {t("trips.history")} ({past.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-4">
          {isLoading ? (
            <div className="grid gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-xl" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <div className="grid place-items-center rounded-xl border border-dashed py-16 text-center">
              <CalendarX className="mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium">{t("trips.emptyTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("trips.emptyBody")}</p>
            </div>
          ) : (
            list.map((r) => {
              const nights = nightsBetween(r.startDate, r.endDate);
              return (
                <Card key={r.id} className="overflow-hidden">
                  <div className="flex flex-col gap-4 p-4 sm:flex-row">
                    <div
                      className="aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg bg-muted sm:w-40"
                      onClick={() => r.product && openProduct(r.product.id)}
                    >
                      {r.product?.images?.[0]?.url ? (
                         
                        <img
                          src={r.product.images[0].url}
                          alt={r.product.title}
                          className="h-full w-full cursor-pointer object-cover"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center text-xs text-muted-foreground">
                          {t("common.noImage")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col justify-between gap-3">
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <button
                              onClick={() => r.product && openProduct(r.product.id)}
                              className="text-left font-semibold hover:underline"
                            >
                              {r.product?.title ?? t("common.loading")}
                            </button>
                            <p className="text-sm text-muted-foreground">
                              {r.product?.location}
                            </p>
                          </div>
                          <Badge className={statusBadgeClass(r.status)}>
                            {statusLabel(r.status, locale)}
                          </Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {formatDate(r.startDate, locale)} → {formatDate(r.endDate, locale)}
                          </span>
                          <span>· {nights} {nightLabel(nights, locale)}</span>
                          <span>· {r.partySize} {t("guests")}</span>
                          <span className="font-medium text-foreground">
                            {formatPrice(r.totalPrice)}
                          </span>
                          {r.bookingType === "SHARED" && (
                            <Badge
                              variant="outline"
                              className={`border ${bookingTypeBadgeClass(r.bookingType)}`}
                            >
                              {bookingTypeLabel(r.bookingType, locale)}
                            </Badge>
                          )}
                        </div>
                        {r.notes && (
                          <p className="mt-2 text-sm text-muted-foreground">“{r.notes}”</p>
                        )}

                        {/* Members sharing a private stay + equal split */}
                        {r.bookingType === "PRIVATE" && r.status !== "CANCELLED" && (
                          <div className="mt-2 rounded-lg border bg-muted/30 p-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="text-xs font-medium text-muted-foreground">
                                {t("stay.members")}:
                              </span>
                              {(r.participants ?? []).map((m: StayMember) => (
                                <span
                                  key={m.id}
                                  className="inline-flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-xs"
                                >
                                  <span className={m.isOrganiser ? "font-medium" : ""}>
                                    {m.name}
                                    {m.isOrganiser ? " · " + t("stay.organiser") : ""}
                                  </span>
                                  <span className="text-muted-foreground">
                                    {formatPrice(m.share)}
                                  </span>
                                  {r.status === "PENDING" &&
                                    !m.isOrganiser &&
                                    m.userId &&
                                    r.userId === user.id && (
                                    <button
                                      type="button"
                                      disabled={busy}
                                      title={t("stay.removeMember")}
                                      onClick={() => removeMember(r.id, m.userId as string)}
                                      className="ml-0.5 text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>

                            {r.status === "PENDING" && r.userId === user.id ? (
                              addFor === r.id ? (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {directory
                                    .filter(
                                      (d) =>
                                        !(r.participants ?? []).some(
                                          (m: StayMember) => m.userId === d.id
                                        )
                                    )
                                    .map((d) => (
                                      <Button
                                        key={d.id}
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={busy}
                                        className="h-7 gap-1 px-2 text-xs"
                                        onClick={() => addMember(r.id, d.id)}
                                      >
                                        <Plus className="h-3 w-3" /> {d.name}
                                      </Button>
                                    ))}
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => setAddFor(null)}
                                  >
                                    {t("common.cancel")}
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="mt-1 h-7 gap-1 px-2 text-xs"
                                  onClick={() => setAddFor(r.id)}
                                >
                                  <Plus className="h-3 w-3" /> {t("stay.addMember")}
                                </Button>
                              )
                            ) : (
                              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                                <Lock className="h-3 w-3" /> {t("stay.locked")}
                              </p>
                            )}
                          </div>
                        )}

                        {r.status === "APPROVED" && r.validator && (
                          <p className="mt-1 text-xs text-emerald-700">
                            {t("trips.validatedBy", { name: r.validator.name })}
                          </p>
                        )}
                      </div>
                      {r.status === "PENDING" && (
                        <Button variant="outline" size="sm" className="w-fit" onClick={() => cancel(r.id)}>
                          {t("trips.cancelRequest")}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
